import type { BatchRegisterRow } from '@api/vendor-portal';
import {
    DataSheet,
    type DataSheetColumn,
    type DataSheetFilterConfig,
    type FilterValue,
} from '@components/ui/DataSheet';
import { DateRangePicker, type UiDateRange } from '@components/ui/DateRange';
import StatusPill from '@components/ui/StatusPill';
import { Button, Checkbox, Group, SegmentedControl, Switch, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import {
    hasDayRange,
    inDayRange,
    NO_DAY_RANGE,
    today as localToday,
} from '@utils/productionWindow';
import { type FC, type ReactNode, useMemo, useState } from 'react';

import { batchQcSplit, qcDisplayStatus, type BatchQcSplit } from './batchQcSplit';
import { batchLabel, canShip, formatDate } from './shipmentRows';


/**
 * A quantity that is usually zero.
 *
 * Muted at zero rather than shown as a dash: these four columns always have an answer, and a
 * dash would read as "not recorded" on a batch that simply has none in that state.
 */
function quantityCell(value: number, tone: string) {
    return value > 0 ? (
        <Text fz={13} c={tone}>
            {value}
        </Text>
    ) : (
        <Text fz={13} c={neutralColors.textMuted}>
            0
        </Text>
    );
}

/**
 * Every verdict a batch can read as, in the order the work moves through them.
 *
 * THE DISPLAY VOCABULARY, NOT THE STORED ONE. Partially passed and Rejected are derived by
 * `qcDisplayStatus` from a stored PASSED plus the rejected quantity — they are what the pill
 * says, so they must be what the picker offers. Filtering the raw three-state disposition
 * instead would leave two of the five words on screen unselectable.
 *
 * Sentence case here, block caps on the pill: a dropdown of SHOUTING enum names reads as
 * machine output, and the list beside it is already doing the work of saying these are states.
 */
const QC_FILTER_ORDER: { value: string; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'PASSED', label: 'Passed' },
    { value: 'PARTIALLY_PASSED', label: 'Partially passed' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'FAILED', label: 'Failed' },
];

const QC_TONE: Record<string, 'positive' | 'warning' | 'critical'> = {
    PASSED: 'positive',
    // Part of the batch was rejected and written off — amber, not green: calling it a clean
    // pass is how a reader concludes 5000 are fine when 2500 are gone.
    PARTIALLY_PASSED: 'warning',
    // Every unit rejected and written off — nothing "partial" about it.
    REJECTED: 'critical',
    PENDING: 'warning',
    FAILED: 'critical',
};

interface BatchRegisterProps {
    rows: BatchRegisterRow[];
    isLoading?: boolean;
    /**
     * Which kind is being listed, because two columns mean different movements per kind.
     *
     * A finished batch is MADE and DISPATCHED; a material batch is RECEIVED and USED. Relabelled
     * rather than shown in both senses under one word, since "dispatched: 0" against a drum of
     * yarn reads as a problem when it is simply not a thing that happens to yarn.
     */
    itemType?: 'product' | 'material';
    /**
     * Overrides for the card's name and count line, for callers that show a slice of the
     * register — the Dispatch tab is these rows filtered to what could still go, and calling
     * that "Batches" would make two tabs claim to be the same thing.
     */
    title?: string;
    subtitle?: string;
    /** Landing here from a QC card pre-filters the list. */
    initialQcFilter?: string | null;
    /** Row click, for acting on a batch where it is listed rather than elsewhere. */
    onSelectBatch?: (row: BatchRegisterRow) => void;
    onKindChange?: (kind: 'product' | 'material') => void;
    onClearQcFilter?: () => void;
    /**
     * The tab strip this sits in, when it is one view among several.
     *
     * Passed through rather than owned, so the register is the same component whether it is a
     * tab on the site's Stock screen or a tab on the brand's view of that site — and so it can
     * stand alone if it ever needs to.
     */
    tabs?: Array<{ id: string; label: string }>;
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    /**
     * Picking batches to put on a shipment.
     *
     * ABSENT MEANS NO COLUMN AT ALL, not a disabled one. The site sees this register too and
     * cannot ship, so leaving greyed checkboxes in front of them would advertise an action they
     * will never be given.
     *
     * The picked set lives with the caller rather than here, so leaving the tab discards it — a
     * selection surviving a tab change is a shipment somebody forgot they had started.
     */
    selection?: {
        picked: Set<string>;
        onToggle: (row: BatchRegisterRow) => void;
        onToggleAll: (rows: BatchRegisterRow[]) => void;
    };
    /**
     * A control the caller adds to the toolbar, beside this register's own.
     *
     * One user so far: the Dispatch tab's by-batch / by-product switch, which has to sit in the
     * same place in both views or it looks like a different control each time. Passed in rather
     * than known here, because the register has no business knowing what else the page can show.
     */
    toolbarExtra?: ReactNode;
}

/**
 * Every run a site has made, one row each.
 *
 * A REGISTER, NOT A STOCK VIEW, and the difference decides what belongs on it. Stock answers
 * "what is here" and so aggregates to the item and drops anything holding zero. This answers
 * "what have we run, and what became of it" — so the grain is the batch, and a run that has
 * entirely shipped is the most complete row on the page rather than an irrelevant one.
 *
 * The columns are deliberately the ones brands already keep in a spreadsheet for this: batch
 * number, when it was made, how much, when it was checked and what the verdict was, when it
 * went and how much of it, and what is left. Downloadable for the same reason — this is meant
 * to replace that spreadsheet, not to ask anybody to abandon it.
 */
const BatchRegister: FC<BatchRegisterProps> = ({
    rows,
    isLoading,
    title,
    subtitle,
    itemType = 'product',
    initialQcFilter,
    onSelectBatch,
    onKindChange,
    onClearQcFilter,
    tabs,
    activeTab,
    onTabChange,
    selection,
    toolbarExtra,
}) => {
    const isProduct = itemType === 'product';

    // Off by default: expired stock is zero on almost every row, so the column earns its width
    // only for somebody who came looking for it.
    const [showExpired, setShowExpired] = useState(false);

    /**
     * The manufacturing window on screen. Empty until somebody picks one, which is the whole
     * register.
     *
     * HELD HERE, SO LEAVING THE TAB DISCARDS IT. Each tab renders this component at its own
     * place in the tree, so moving between Batches, QC Pending and Dispatch resets the dates —
     * the same way the Expired switch and the picked-for-a-lorry set already behave. A window
     * that followed somebody onto a tab they did not set it on is a short list they read as
     * the whole one.
     */
    const [madeRange, setMadeRange] = useState<UiDateRange>(NO_DAY_RANGE);

    /**
     * The verdicts on screen. Empty means every one of them, which is how the sheet opens.
     *
     * MULTI, BECAUSE THE QUESTIONS PEOPLE ASK HERE ARE UNIONS. "What still needs looking at"
     * is Pending; "what went wrong" is Rejected and Failed together; "what can I count on" is
     * Passed and Partially passed. A single-choice control would make the second and third of
     * those two passes over the same sheet.
     */
    const [qcFilter, setQcFilter] = useState<FilterValue>({});
    const qcPicks = qcFilter.qc ?? [];

    /**
     * One split per row, computed once for the four QC columns that read it.
     *
     * `today` is captured per render rather than per cell so every column on a row agrees about
     * what has expired — a render that straddled midnight could otherwise disagree with itself.
     */
    const splitOf = useMemo(() => {
        const today = localToday();
        const cache = new Map<string, BatchQcSplit>();

        return (row: BatchRegisterRow): BatchQcSplit => {
            const hit = cache.get(row.batchId);
            if (hit) return hit;

            const split = batchQcSplit(row, today);
            cache.set(row.batchId, split);

            return split;
        };
        // `rows` is not read in the body - it is what invalidates the per-batch cache when the
        // data changes. Dropping it, as the rule suggests, would keep serving splits computed
        // from the previous fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    // Finished goods only. Material leaves by being consumed, returned or written off — never
    // dispatched — so a pick column on the materials view would offer a shipment of yarn.
    const picking = selection && isProduct ? selection : null;

    /**
     * Filtered here rather than by the grid's own column filter when somebody arrived from a
     * card, because they have already said what they want to see and should not have to say it
     * again in a menu.
     */
    const byQc = initialQcFilter
        ? rows.filter((row) => row.qcStatus === initialQcFilter)
        : rows;
    /**
     * And then to the window somebody picked, if they picked one.
     *
     * ON `manufacturingDate`, WHICHEVER TAB THIS IS. The register is read to answer "what did
     * we run that week" — of the four dates on a row it is the only one every tab shares a
     * meaning for, and the only one a person reaching for a calendar here has in mind.
     *
     * Filtered into `visible` rather than left to the grid, so the count in the heading, the
     * rows a pick-all takes and the CSV all describe one list. A filter the export ignored
     * would send somebody a spreadsheet that disagreed with their screen.
     */
    const byDate = hasDayRange(madeRange)
        ? byQc.filter((row) => inDayRange(row.manufacturingDate, madeRange))
        : byQc;
    /**
     * And last to the verdicts somebody ticked.
     *
     * A batch with no verdict at all resolves to null and so is never in a pick — which is
     * right: "show me the failed ones" should not hand back rows nobody has looked at yet.
     */
    const visible = qcPicks.length
        ? byDate.filter((row) => qcPicks.includes(qcDisplayStatus(row) ?? ''))
        : byDate;

    /**
     * Only the verdicts this sheet actually holds.
     *
     * Offering all five everywhere would put Rejected in front of a site that has never
     * rejected anything, and picking it would empty the table — the same reasoning the
     * Production tab's status control already follows.
     *
     * Read off `byQc` rather than the date-filtered rows, so narrowing dates does not make
     * options appear and vanish under the cursor while somebody is choosing.
     */
    const qcOptions = QC_FILTER_ORDER.filter((option) =>
        byQc.some((row) => qcDisplayStatus(row) === option.value),
    );

    /**
     * Hidden where a TAB has already fixed the verdict.
     *
     * QC Pending is `initialQcFilter` with nothing to clear it by — the tab is the filter — so
     * a picker there would offer four choices that empty the sheet and one that changes
     * nothing. Arriving from a card passes `onClearQcFilter` as well, and that is a filter
     * somebody can step out of, so the picker stays.
     */
    const qcPinnedByTab = !!initialQcFilter && !onClearQcFilter;

    /**
     * THE HEADER BAR'S OWN FILTER, not a Mantine MultiSelect in the toolbar.
     *
     * Same control the Sales card uses for Channel and Product, so a person who has filtered
     * one grid has already learnt this one. It also stays one control-height whatever is
     * ticked — a MultiSelect renders a pill per selection and wraps onto a second line, which
     * in a row of 30px controls drags the whole toolbar out of line.
     */
    const qcFilterConfig: DataSheetFilterConfig = {
        label: 'QC',
        params: [
            {
                key: 'qc',
                label: 'QC status',
                mode: 'multi',
                options: qcOptions,
            },
        ],
        value: qcFilter,
        onChange: setQcFilter,
    };
    /** Dropped for want of a date rather than for falling outside the window — see below. */
    const undated = hasDayRange(madeRange)
        ? byQc.filter((row) => !row.manufacturingDate).length
        : 0;
    /**
     * What the toolbar filters did, appended to whatever the heading already said.
     *
     * BECAUSE THE CALLER'S COUNT IS NOT RECOMPUTED. Dispatch and QC Pending pass their own
     * subtitle, worked out from the rows they handed in — so with a window on, the heading
     * would go on claiming "12 batches awaiting dispatch" above three of them. This says what
     * is really on screen without touching their wording.
     *
     * The undated tail is called out rather than swallowed. A site that has never recorded a
     * manufacturing date would otherwise pick a window, get an empty sheet, and reasonably
     * conclude the screen is broken.
     */
    const filterNote =
        isLoading || !(hasDayRange(madeRange) || qcPicks.length)
            ? ''
            : ` · ${visible.length} shown` +
              (undated > 0 ? `, ${undated} without an MFG. Date hidden` : '');
    /** The rows a pick-all would take: what is on screen now, minus what cannot go. */
    // Not point-free: filter would hand the array index to canShip's `today` param.
    const pickable = picking ? visible.filter((row) => canShip(row)) : [];
    const allPicked =
        pickable.length > 0 && pickable.every((row) => picking?.picked.has(row.batchId));

    const columns: DataSheetColumn<BatchRegisterRow>[] = [
        ...(picking
            ? [
                  {
                      key: '_pick',
                      header: '',
                      width: 48,
                      sticky: true,
                      sortable: false,
                      filterable: false,
                      csvValue: () => '',
                      // The header tick takes the whole filtered view, so narrowing the list
                      // first and then picking all is the fast path for "send everything that
                      // passed".
                      renderHeader: () => (
                          <Checkbox
                              size="xs"
                              aria-label="Pick every batch that can ship"
                              checked={allPicked}
                              indeterminate={
                                  !allPicked &&
                                  pickable.some((row) => picking.picked.has(row.batchId))
                              }
                              disabled={pickable.length === 0}
                              onChange={() => picking.onToggleAll(pickable)}
                          />
                      ),
                      render: (_value: unknown, row: BatchRegisterRow) =>
                          canShip(row) ? (
                              <Checkbox
                                  size="xs"
                                  aria-label={`Put ${batchLabel(row)} on the shipment`}
                                  checked={picking.picked.has(row.batchId)}
                                  onChange={() => picking.onToggle(row)}
                                  // The row itself opens the QC panel. Without this, ticking a
                                  // box would tick it and then open a panel over the list.
                                  onClick={(event) => event.stopPropagation()}
                              />
                          ) : null,
                  },
              ]
            : []),
        {
            key: 'code',
            header: 'Batch',
            width: 150,
            sticky: true,
            filterable: true,
            // Bucket stock has no code. Said in words and muted, rather than left blank: an
            // empty cell in the sticky first column reads as a row that failed to load.
            render: (value: string, row: BatchRegisterRow) =>
                row.noBatch ? (
                    <Text fz={13} c={neutralColors.textMuted}>
                        No batch
                    </Text>
                ) : (
                    value
                ),
            valueAccessor: (row: BatchRegisterRow) => batchLabel(row),
            csvValue: (_value: string, row: BatchRegisterRow) => batchLabel(row),
        },
        {
            key: 'itemId',
            header: 'Item',
            width: 240,
            filterable: true,
            render: (value: string | null, row) => row.itemName ?? value ?? '—',
            valueAccessor: (row) => row.itemName ?? row.itemId ?? '',
            csvValue: (value: string | null, row) => row.itemName ?? value ?? '',
        },
        {
            key: 'manufacturingDate',
            // MFG. is the abbreviation the industry already reads; the full word
            // was the widest header on the sheet and forced its column to match.
            header: 'MFG. Date',
            width: 130,
            render: (value: string | null) => formatDate(value),
            csvValue: (value: string | null) => value ?? '',
        },
        {
            key: 'made',
            header: isProduct ? 'MFG.' : 'Received',
            width: 100,
            align: 'right',
        },
        {
            key: 'qcStatus',
            header: 'QC',
            width: 130,
            filterable: true,
            /**
             * The DISPLAY status, not the stored one: a pass with rejects reads "Partially
             * passed". Derived in one shared rule so the pill, the filter, the sort and the
             * CSV all say the same word — the raw three-state disposition stays on the row
             * for anything that gates on it (the pending-filter pivot included).
             */
            valueAccessor: (row: BatchRegisterRow) => qcDisplayStatus(row) ?? '',
            render: (_value: string | null, row: BatchRegisterRow) => {
                const display = qcDisplayStatus(row);
                return display ? (
                    <StatusPill
                        label={display.replace('_', ' ')}
                        tone={QC_TONE[display] ?? 'neutral'}
                    />
                ) : (
                    '—'
                );
            },
            csvValue: (_value: string | null, row: BatchRegisterRow) =>
                qcDisplayStatus(row) ?? '',
        },
        {
            key: 'qcMarkedAt',
            header: 'QC Date',
            width: 130,
            render: (value: string | null) => formatDate(value),
            csvValue: (value: string | null) => value ?? '',
        },
        /**
         * How the quantity divides across QC states — see batchQcSplit.
         *
         * Derived rather than read off the row: the batch stores a verdict, not four totals,
         * and a whole-batch verdict leaves both quantity columns null. UNIQUE KEYS with a
         * valueAccessor, not borrowed row keys: these columns once reused `made` and
         * `qcStatus` as their keys, which collided with the real QC column — the filter
         * popover tracks openness BY KEY, so one click opened two popovers, and sorting
         * "QC pending" silently sorted by the verdict string.
         */
        {
            key: 'qcApprovedQty',
            header: 'QC approved',
            width: 130,
            align: 'right',
            valueAccessor: (row: BatchRegisterRow) => splitOf(row).approved,
            render: (_value: number, row: BatchRegisterRow) =>
                quantityCell(splitOf(row).approved, semanticColors.positive.dark),
            csvValue: (_value: number, row: BatchRegisterRow) =>
                String(splitOf(row).approved),
        },
        {
            key: 'qcPendingQty',
            header: 'QC pending',
            width: 120,
            align: 'right',
            valueAccessor: (row: BatchRegisterRow) => splitOf(row).pending,
            render: (_value: number, row: BatchRegisterRow) =>
                quantityCell(splitOf(row).pending, semanticColors.warning.dark),
            csvValue: (_value: number, row: BatchRegisterRow) =>
                String(splitOf(row).pending),
        },
        {
            key: 'qcFailedQuantity',
            header: 'Rejected',
            width: 100,
            align: 'right',
            valueAccessor: (row: BatchRegisterRow) => splitOf(row).rejected,
            render: (_value: number | null, row: BatchRegisterRow) =>
                quantityCell(splitOf(row).rejected, semanticColors.critical.dark),
            csvValue: (_value: number | null, row: BatchRegisterRow) =>
                String(splitOf(row).rejected),
        },
        /**
         * Passed QC and then went out of date. Hidden unless asked for: it is zero on almost
         * every row, and a column of dashes earns none of the width it takes.
         */
        ...(showExpired
            ? [
                  {
                      key: 'expiryDate',
                      header: 'Expired',
                      width: 130,
                      align: 'right' as const,
                      render: (_value: string | null, row: BatchRegisterRow) =>
                          quantityCell(splitOf(row).expired, semanticColors.critical.dark),
                      csvValue: (_value: string | null, row: BatchRegisterRow) =>
                          String(splitOf(row).expired),
                  },
              ]
            : []),
        {
            key: 'dispatched',
            header: isProduct ? 'Dispatched' : 'Used',
            width: 110,
            align: 'right',
        },
        {
            key: 'lastDispatchedOn',
            header: isProduct ? 'Dispatched On' : 'Last used',
            width: 130,
            render: (value: string | null) => formatDate(value),
            csvValue: (value: string | null) => value ?? '',
        },
        /**
         * The real on-hand balance, NOT manufactured minus dispatched.
         *
         * It is read from the stock ledger, so it also carries QC rejections written off,
         * wastage, adjustments and reversals — a batch of 1,000 with 100 rejected and nothing
         * shipped reads 900 here while the arithmetic would say 1,000. That is exactly why the
         * column cannot be dropped and reconstructed from its neighbours.
         */
        {
            key: 'remaining',
            header: 'On Hand',
            width: 110,
            align: 'right',
            // Muted at zero: a run that has entirely gone is finished, not a problem, and it
            // should read as the quiet end of a row rather than as an empty shelf.
            render: (value: number) =>
                value > 0 ? (
                    value
                ) : (
                    <Text fz={13} c={neutralColors.textMuted}>
                        0
                    </Text>
                ),
        },
        {
            key: 'expiryDate',
            header: 'Expires',
            width: 130,
            render: (value: string | null) => formatDate(value),
            csvValue: (value: string | null) => value ?? '',
        },
    ];

    return (
        <DataSheet<BatchRegisterRow>
            data={visible}
            columns={columns}
            title={title ?? 'Batches'}
            subtitle={
                (subtitle ??
                    (isLoading
                        ? 'Loading…'
                        : initialQcFilter
                          ? `${visible.length} batch${visible.length === 1 ? '' : 'es'} ${initialQcFilter.toLowerCase()}`
                          : isProduct
                            ? `${visible.length} run${visible.length === 1 ? '' : 's'}, newest first. Runs that have entirely shipped are kept.`
                            : `${visible.length} batch${visible.length === 1 ? '' : 'es'}, newest first. Batches entirely used up are kept.`)) +
                filterNote
            }
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            /*
             * Named, rather than leaving this to the QC column's own header filter. That one is
             * a condition builder over free text: the pill reads "PARTIALLY PASSED" while the
             * value behind it is PARTIALLY_PASSED, so typing what is on screen matches nothing
             * — and its default operator, Contains, makes "PASSED" quietly match the partial
             * ones too.
             *
             * Withheld with fewer than two verdicts on the sheet: a picker offering one option
             * can only do nothing.
             */
            filters={
                qcPinnedByTab || qcOptions.length < 2 ? undefined : [qcFilterConfig]
            }
            enableSearch
            searchPlaceholder="Batch or item…"
            toolbarControls={
                <Group gap={8}>
                    {/* The same calendar the Sales card uses, on the column this sheet is
                        actually read by. The reset only appears once a window is set, so it
                        always means "back to every run" rather than sitting there inert. */}
                    <DateRangePicker
                        value={madeRange}
                        onChange={setMadeRange}
                        onReset={
                            hasDayRange(madeRange)
                                ? () => setMadeRange(NO_DAY_RANGE)
                                : undefined
                        }
                        /*
                         * Not relabelled per kind, unlike the quantity columns: the MFG. Date
                         * column keeps its name for material too, and a tooltip saying
                         * "received" would name a column this sheet does not have.
                         */
                        title="Filter by MFG. Date"
                    />
                    {/* A switch rather than a column picker: exactly one column is optional
                        here, and a menu to toggle one thing is a menu nobody opens. */}
                    <Switch
                        size="xs"
                        label="Expired"
                        checked={showExpired}
                        onChange={(event) => setShowExpired(event.currentTarget.checked)}
                    />
                    {toolbarExtra}
                    {/* QC is indifferent to how a thing got here, so the register carries both
                        kinds. Material leaves by being consumed rather than dispatched, which
                        is why two of the columns are relabelled rather than reused. */}
                    {onKindChange && (
                        <SegmentedControl
                            size="xs"
                            value={itemType}
                            onChange={(value) =>
                                onKindChange(value as 'product' | 'material')
                            }
                            data={[
                                { value: 'product', label: 'Finished goods' },
                                { value: 'material', label: 'RM/PM' },
                            ]}
                        />
                    )}
                    {initialQcFilter && onClearQcFilter && (
                        <Button
                            size="xs"
                            variant={ButtonVariant.SUBTLE}
                            onClick={onClearQcFilter}
                        >
                            Clear {initialQcFilter.toLowerCase()} filter
                        </Button>
                    )}
                </Group>
            }
            getRowKey={(row) => row.batchId}
            // A no-batch row has no QC panel to open: there is no batch to rule on.
            onRowClick={
                onSelectBatch
                    ? (row) => {
                          if (!row.noBatch) onSelectBatch(row);
                      }
                    : undefined
            }
            enableDownload
            downloadFilename="batch-register.csv"
            maxHeight="auto"
        />
    );
};

export default BatchRegister;
